import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  EmptyState,
  Skeleton,
  ErrorState,
  Alert,
} from '@/components/ui'
import { Shield, Plus, Edit2, Trash2, Users } from 'lucide-react'
import { api, type AppRoleInfo } from '@/api/client'

export function AppRolesPage() {
  const { orgSlug, appId } = useParams()
  const [roles, setRoles] = useState<AppRoleInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleSlug, setNewRoleSlug] = useState('')
  const [newRoleDesc, setNewRoleDesc] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  useEffect(() => {
    fetchRoles()
  }, [orgSlug, appId])

  const fetchRoles = async () => {
    if (!orgSlug || !appId) return

    try {
      setError(null)
      const response = await api.apps.listRoles(orgSlug, appId)
      setRoles(response.roles)
    } catch (err) {
      console.error('Failed to fetch roles:', err)
      setError(err instanceof Error ? err.message : 'Failed to load roles')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateRole = async () => {
    if (!orgSlug || !appId || !newRoleName || !newRoleSlug) return

    setIsCreating(true)
    setCreateError(null)

    try {
      await api.apps.createRole(orgSlug, appId, {
        slug: newRoleSlug,
        name: newRoleName,
        description: newRoleDesc || undefined,
        scopes: [],
      })
      setCreateDialogOpen(false)
      setNewRoleName('')
      setNewRoleSlug('')
      setNewRoleDesc('')
      fetchRoles()
    } catch (err) {
      console.error('Failed to create role:', err)
      setCreateError(err instanceof Error ? err.message : 'Failed to create role')
    } finally {
      setIsCreating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-48" />
          <Button disabled>
            <Plus className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div>
                      <Skeleton className="h-5 w-24 mb-2" />
                      <Skeleton className="h-4 w-48 mb-3" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-5 w-16" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[var(--text-secondary)]">
            Roles define what users can do in your app
          </p>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        </div>
        <ErrorState
          title="Failed to load roles"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--text-secondary)]">
            Roles define what users can do in your app
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Role
        </Button>
      </div>

      {/* Roles list */}
      {roles.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No roles yet"
          description="Create roles to organize permissions for your app users"
          action={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Role
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-[var(--bg-elevated-2)] p-2">
                      <Shield className="h-5 w-5 text-[var(--text-secondary)]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-[var(--text-primary)]">
                          {role.name}
                        </h3>
                        {role.is_system && (
                          <Badge variant="info">System</Badge>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-[var(--text-secondary)]">
                        {role.description || 'No description'}
                      </p>
                      <p className="mt-1 text-xs text-[var(--text-muted)] font-mono">
                        {role.slug}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {role.scopes.length > 0 ? (
                          role.scopes.map((scope) => (
                            <Badge key={scope} variant="default">
                              {scope}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">No scopes assigned</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!role.is_system && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
            <DialogDescription>
              Define a new role for your app users
            </DialogDescription>
          </DialogHeader>

          {createError && (
            <Alert variant="error" className="my-2" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}

          <div className="space-y-4 py-4">
            <Input
              label="Role Name"
              placeholder="Moderator"
              value={newRoleName}
              onChange={(e) => {
                setNewRoleName(e.target.value)
                if (!newRoleSlug) {
                  setNewRoleSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
                }
              }}
              disabled={isCreating}
            />
            <Input
              label="Slug"
              placeholder="moderator"
              value={newRoleSlug}
              onChange={(e) => setNewRoleSlug(e.target.value)}
              hint="Lowercase, no spaces"
              disabled={isCreating}
            />
            <Input
              label="Description"
              placeholder="Can moderate content"
              value={newRoleDesc}
              onChange={(e) => setNewRoleDesc(e.target.value)}
              disabled={isCreating}
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreateRole} loading={isCreating}>
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
